import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Check, Zap, Loader2, ArrowRight } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Static plan display metadata. Prices/entitlements source-of-truth is the backend,
// but we surface a curated, marketing-tuned feature list here.
const PLAN_DISPLAY = {
  free: {
    name: 'Free',
    tagline: '2 generations/month',
    monthly_price: 0,
    annual_price: 0,
    plan_id_monthly: 'free',
    plan_id_annual: 'free',
    features: [
      '2 Creataby generations per month',
      '1 active project',
      'All 3 income paths',
      'Step-by-step guidance',
      'Export to funnel builders',
    ],
  },
  creator: {
    name: 'Creator',
    tagline: 'Create and launch your first digital products',
    monthly_price: 39,
    annual_price: 390,
    plan_id_monthly: 'creator_monthly',
    plan_id_annual: 'creator_annual',
    features: [
      'Unlimited Creataby generations',
      'Up to 3 active projects',
      'All 3 income paths',
      'Full product generation (ebooks & workbooks)',
      'Export to PDF',
      'Cover concept generation',
      'Standard sales page',
      '5-email launch sequence',
      '1 brand kit',
      'Save, organize & regenerate outputs',
    ],
  },
  creator_pro: {
    name: 'Creator Pro',
    tagline: 'Build a full catalog & full launch campaigns',
    monthly_price: 79,
    annual_price: 790,
    plan_id_monthly: 'creator_pro_monthly',
    plan_id_annual: 'creator_pro_annual',
    features: [
      'Everything in Creator, plus:',
      'Unlimited active projects',
      'All product types (template packs, lead magnets, bundles)',
      'Advanced graphics generation',
      'Full launch campaigns',
      'Export to ClickFunnels & Systeme.io',
      'Duplicate & repurpose projects',
      'Up to 5 client workspaces',
      'Unlimited brand kits',
      'Version history',
      'Priority support',
    ],
  },
};

const PLAN_ORDER = ['free', 'creator', 'creator_pro'];

const Pricing = () => {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState('monthly'); // 'monthly' | 'annual'
  const [currentPlanId, setCurrentPlanId] = useState('free');
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const token = session?.access_token;
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await fetch(`${API}/subscription/plans`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          const current = data.find((p) => p.is_current);
          if (current) setCurrentPlanId(current.id);
        }
      } catch (error) {
        process.env.NODE_ENV === 'development' && console.error('Error fetching plans:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [session]);

  const isCurrentPlan = (displayKey) => {
    if (!session) return false;
    if (displayKey === 'free') return currentPlanId === 'free';
    const p = PLAN_DISPLAY[displayKey];
    return currentPlanId === p.plan_id_monthly || currentPlanId === p.plan_id_annual;
  };

  const handleUpgrade = async (displayKey) => {
    const token = session?.access_token;
    if (!token) {
      navigate('/login');
      return;
    }

    const p = PLAN_DISPLAY[displayKey];
    const planId = billing === 'annual' ? p.plan_id_annual : p.plan_id_monthly;

    setCheckoutLoading(displayKey);
    try {
      const response = await fetch(`${API}/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create checkout');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      process.env.NODE_ENV === 'development' && console.error('Checkout error:', error);
      toast.error(error.message || 'Failed to start checkout');
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
      </div>
    );
  }

  const isAuthed = !!session;

  const inner = (
    <>
      <Toaster position="top-center" richColors />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-6"
      >
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-2"
          data-testid="pricing-title"
        >
          Choose Your Plan
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base">
          Start free. Upgrade when you need more generations.
        </p>
      </motion.div>

      {/* Billing toggle */}
      <div className="flex justify-center mb-8">
        <div
          className="inline-flex items-center bg-secondary rounded-full p-1 border border-border"
          data-testid="billing-toggle"
        >
          <button
            type="button"
            onClick={() => setBilling('monthly')}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
              billing === 'monthly'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="billing-toggle-monthly"
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBilling('annual')}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
              billing === 'annual'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="billing-toggle-annual"
          >
            Annual
            <span className="text-[10px] font-bold uppercase tracking-wider bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
              Save 17%
            </span>
          </button>
        </div>
      </div>

      {/* Pricing cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PLAN_ORDER.map((key, index) => {
          const plan = PLAN_DISPLAY[key];
          const isPopular = key === 'creator_pro';
          const isFree = key === 'free';
          const isCurrent = isCurrentPlan(key);

          const rawPrice =
            billing === 'annual' && !isFree
              ? plan.annual_price / 12
              : plan.monthly_price;
          const displayPrice = Number.isInteger(rawPrice)
            ? rawPrice
            : rawPrice.toFixed(2);

          const priceSuffix = isFree ? '' : '/month';
          const annualNote =
            billing === 'annual' && !isFree
              ? `Billed $${plan.annual_price}/year`
              : null;

          return (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08 }}
              whileHover={{ y: -3 }}
            >
              <Card
                className={`relative h-full flex flex-col overflow-hidden rounded-lg transition-all ${
                  isPopular
                    ? 'border-0 shadow-[0_8px_32px_rgba(14,129,117,0.25)]'
                    : 'border border-border shadow-[0_4px_20px_rgba(0,0,0,0.06)]'
                }`}
                data-testid={`plan-card-${key}`}
              >
                {/* Header block */}
                {isPopular ? (
                  <div className="bg-gradient-to-br from-teal-600 to-emerald-700 px-6 pt-10 pb-6 text-white relative">
                    <div className="absolute top-3 left-1/2 -translate-x-1/2">
                      <span
                        className="bg-white/25 backdrop-blur-sm text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full"
                        data-testid="most-popular-badge"
                      >
                        Most Popular
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-white" data-testid={`plan-price-${key}`}>
                        ${displayPrice}
                      </span>
                      <span className="text-white/80 text-sm">{priceSuffix}</span>
                    </div>
                    <p className="text-sm text-white/90 mt-1">
                      {annualNote || plan.tagline}
                    </p>
                  </div>
                ) : (
                  <div className="px-6 pt-6 pb-4 bg-background">
                    <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-foreground" data-testid={`plan-price-${key}`}>
                        ${displayPrice}
                      </span>
                      {!isFree && (
                        <span className="text-muted-foreground text-sm">{priceSuffix}</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {annualNote || plan.tagline}
                    </p>
                  </div>
                )}

                <CardContent className="flex-1 flex flex-col px-6 pt-5 pb-6">
                  <ul className="space-y-2.5 mb-6 flex-1">
                    {plan.features.map((feature, i) => {
                      const isSectionHeader = feature.endsWith(':');
                      return (
                        <li
                          key={`${key}-feat-${i}`}
                          className={`flex items-start gap-2 text-sm ${
                            isSectionHeader ? 'font-semibold pt-1' : ''
                          }`}
                        >
                          {!isSectionHeader && (
                            <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                          )}
                          <span
                            className={
                              isSectionHeader ? 'text-foreground' : 'text-foreground/90'
                            }
                          >
                            {feature}
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  {isCurrent ? (
                    <Button
                      disabled
                      className="w-full bg-secondary text-muted-foreground"
                      data-testid={`plan-btn-${key}`}
                    >
                      Current Plan
                    </Button>
                  ) : isFree ? (
                    !isAuthed ? (
                      <Button
                        onClick={() => navigate('/login')}
                        variant="outline"
                        className="w-full border-border"
                        data-testid={`plan-btn-${key}`}
                      >
                        Start free
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full border-border"
                        disabled
                        data-testid={`plan-btn-${key}`}
                      >
                        Free Forever
                      </Button>
                    )
                  ) : (
                    <Button
                      onClick={() => handleUpgrade(key)}
                      disabled={checkoutLoading === key}
                      className={`w-full font-semibold ${
                        isPopular
                          ? 'bg-creataby-gradient-btn text-white hover:opacity-95 creataby-shimmer shadow-[0_2px_12px_rgba(14,129,117,0.25)]'
                          : 'bg-foreground text-background hover:bg-foreground/90'
                      }`}
                      data-testid={`plan-btn-${key}`}
                    >
                      {checkoutLoading === key ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Zap className="mr-2 h-4 w-4" />
                          {isAuthed ? `Upgrade to ${plan.name}` : `Start with ${plan.name}`}
                        </>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      <p className="text-center text-xs text-muted-foreground mt-8">
        Cancel anytime. Prices in USD. Annual plans billed once yearly.
      </p>
    </>
  );

  // Authenticated users get the full app shell.
  if (isAuthed) {
    return <AppLayout maxWidth="max-w-6xl">{inner}</AppLayout>;
  }

  // Public visitors get a minimal marketing shell (matches Landing).
  return (
    <div className="min-h-screen bg-white">
      <header
        className="sticky top-0 z-40 border-b border-slate-100 backdrop-blur-md"
        style={{ backgroundColor: 'rgba(255,255,255,0.85)' }}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-nav-logo">
            <img src="/banner-logo.png" alt="Creataby" className="h-10" />
          </Link>
          <nav className="flex items-center gap-1 sm:gap-3">
            <Link to="/docs">
              <Button variant="ghost" size="sm" className="text-sm text-slate-600 hover:text-slate-900">
                Guides
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" size="sm" className="text-sm text-slate-600 hover:text-slate-900">
                Sign in
              </Button>
            </Link>
            <Link to="/login">
              <Button
                size="sm"
                className="text-sm font-semibold rounded-full px-4 bg-teal-600 hover:bg-teal-700 text-white shadow-[0_2px_10px_rgba(22,170,168,0.35)]"
                data-testid="pricing-nav-cta"
              >
                Start free <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
          </nav>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">{inner}</main>
    </div>
  );
};

export default Pricing;
